import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';
import WeightLineChart from '../WeightLineChart.js';
import { compareWeighins } from '../weighins.js';
import '../lots/WeightLots.css';

const formatSessionDate = (dateString) => {
  if (!dateString) return '—';
  const s = dateString.length === 10 ? dateString + 'T12:00:00' : dateString;
  const d = new Date(s);
  if (isNaN(d)) return dateString;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;
};

const formatLotCode = (lotId, prefix) =>
  lotId != null ? `${prefix}-${String(lotId).padStart(4, '0')}` : '—';

const StatCard = ({ label, value, onClick }) => (
  <div className={`wl-stat-card${onClick ? ' wl-stat-card-editable' : ''}`} onClick={onClick}>
    <div className="wl-stat-label">{label}</div>
    <div className="wl-stat-value" style={onClick ? { color: '#b8862f' } : undefined}>
      {value ?? '—'}
    </div>
  </div>
);

const WeightAnimalDetail = () => {
  const { tag }  = useParams();
  const navigate = useNavigate();
  const { t }    = useTranslation();

  const [enrollment, setEnrollment] = useState(null);
  const [weights,    setWeights]    = useState([]);
  const [lot,        setLot]        = useState(null);

  useEffect(() => {
    Fetch(`api/data/lots.animals?search_fields=type,tag&search_values=weight,${tag}`, 'get', null,
      (data) => { const r = (data || [])[0]; if (r) setEnrollment(r); }
    );
    Fetch(`api/data/weights?search_fields=tag&search_values=${tag}`, 'get', null,
      (data) => setWeights((data || []).filter(w => w.date).sort(compareWeighins))
    );
  }, [tag]);

  useEffect(() => {
    if (!enrollment?.lot_id) return;
    Fetch(`api/data/lots?search_fields=type,lot_id&search_values=weight,${enrollment.lot_id}`, 'get', null,
      (data) => { const found = (data || [])[0]; if (found) setLot(found); }
    );
  }, [enrollment]);

  const first  = weights[0]  ?? null;
  const latest = weights[weights.length - 1] ?? null;

  const adg = (() => {
    if (!first || !latest || first.date === latest.date) return null;
    const days = (new Date(latest.date) - new Date(first.date)) / 86400000;
    return days > 0 ? (latest.weight - first.weight) / days : null;
  })();

  const weighinsLabel = weights.length > 0
    ? `${weights.length} · ${t('last_weigh_in_short')} ${latest.weight} kg`
    : t('no_weigh_ins');

  return (
    <div className="wld-page">

      {/* Header */}
      <div className="wld-header">
        <div className="wld-header-top">
          <div className="wl-detail-code">{t('tag')} #{tag}</div>
        </div>
        {enrollment?.lot_id && (
          <div className="wld-meta">
            <span className="wld-lot-link" onClick={() => navigate(`/weight/lots/${enrollment.lot_id}`)}>
              {formatLotCode(enrollment.lot_id, t('weight_lot_prefix'))}
            </span>
            {lot?.name && <span style={{ color: '#aaa' }}> · {lot.name}</span>}
          </div>
        )}
      </div>

      {/* Stat cards */}
      <div className="wl-stats-grid">
        <StatCard label={t('first_weigh_date')}  value={first  ? formatSessionDate(first.date)  : null} />
        <StatCard label={t('first_weigh_value')} value={first  ? `${first.weight} kg`           : null} />
        <StatCard label={t('last_weigh_date')}   value={latest ? formatSessionDate(latest.date) : null} />
        <StatCard label={t('last_weigh_value')}  value={latest ? `${latest.weight} kg`          : null} />
        <StatCard label={t('adg_column')}        value={adg != null ? `${adg.toFixed(2)} kg/d`  : null} />
      </div>

      <WeightLineChart
        points={weights.map(w => ({ date: w.date, weight: w.weight }))}
        label={`${t('tag')} #${tag}`}
      />

      {/* Nav to weigh-in history */}
      <div className="wld-nav-list" style={{ marginTop: 16 }}>
        <div className="wld-nav-row" onClick={() => navigate(`/weight/animals/${tag}/weighins`)}>
          <span className="wld-nav-label">{t('total_weigh_ins')}</span>
          <div className="wld-nav-right">
            <span className="wld-nav-summary">{weighinsLabel}</span>
            <span className="wld-nav-chevron">›</span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default WeightAnimalDetail;
